import { useEffect, useState } from "react";
import { CheckCircle2, UserRound, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { BuyerReliability } from "@/components/TrustBadges";

export function SoldDialog({ listing, open, onOpenChange, onDone }) {
  const [buyers, setBuyers] = useState(null);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setPick(null); api.get(`/listings/${listing.id}/buyers`).then((r) => setBuyers(r.data)); }
  }, [open, listing.id]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/listings/${listing.id}/status`, { status: "sold", buyer_id: pick });
      toast.success(pick && pick !== "offsite" ? "Sold! Your buyer can now rate you." : "Marked as sold — nice one!");
      onOpenChange(false); onDone(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  const Option = ({ id, label, icon: Icon, testId, stats }) => (
    <button data-testid={testId} onClick={() => setPick(id)} className={`flex w-full flex-wrap items-center gap-3 rounded-xl px-4 py-3 text-left text-sm font-semibold transition-colors ${pick === id ? "bg-[#d4f253] text-[#0f2a33]" : "bg-white/5 hover:bg-white/10"}`}>
      <Icon className="h-4 w-4" /> <span className="mr-auto">{label}</span>
      {stats && <BuyerReliability stats={stats} testId={`${testId}-reliability`} />}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sold-dialog" className="border-white/10 bg-[#173742] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">Who bought "{listing.title}"?</DialogTitle>
          <DialogDescription className="text-slate-400">Only the buyer you pick will be invited to rate you.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {buyers === null && <p className="text-sm text-slate-400">Loading buyers…</p>}
          {buyers?.length === 0 && <p data-testid="sold-no-buyers" className="text-sm text-slate-400">No one has messaged you about this item yet.</p>}
          {buyers?.map((b) => <Option key={b.id} id={b.id} label={b.name} icon={UserRound} testId={`sold-buyer-${b.id}`} stats={b} />)}
          <Option id="offsite" label="Someone else / not on ISoldIt" icon={Users} testId="sold-buyer-offsite" />
        </div>
        <button data-testid="sold-confirm-btn" disabled={!pick || busy} onClick={submit} className="btn-lime w-full py-3 text-sm"><CheckCircle2 className="h-4 w-4" />Mark as sold</button>
      </DialogContent>
    </Dialog>
  );
}
